import { Link2 } from 'lucide-react';
import RecoveryMinSecInputs from '../common/RecoveryMinSecInputs';
import PyramidSeriesEditor from '../common/PyramidSeriesEditor';
import TemplateRepsInputs from '../common/TemplateRepsInputs';
import { GYM_WORKOUT_SET_TYPE_OPTIONS } from '../../../constants/workoutSetTypes';
import { isPyramidExercise } from '../../../constants/workoutSeriesPlan';

const labelClass = 'mb-1 block text-xs text-gray-500 dark:text-white/65';

const noSpinner =
  '[appearance:textfield] [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';

const controlClass =
  `rounded border border-gray-300 bg-white text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-white/30 ${noSpinner}`;

const numClass = `w-16 shrink-0 px-1.5 py-1.5 text-center tabular-nums ${controlClass}`;
const weightClass = `w-20 shrink-0 px-1.5 py-1.5 text-center tabular-nums ${controlClass}`;
const selectClass = `w-40 max-w-full shrink-0 px-2 py-1.5 ${controlClass}`;

function Field({ label, children }) {
  return (
    <div className="min-w-0">
      <label className={labelClass}>{label}</label>
      {children}
    </div>
  );
}

/**
 * Parametri di una riga esercizio nel template: numeri stretti, tipo e note a parte.
 */
export default function TemplateExerciseFields({
  exercise,
  index,
  onUpdate,
  selectedExercises,
  rowId,
}) {
  const isCardio = exercise.is_cardio === 1 || exercise.is_cardio === true;
  const pyramid = isPyramidExercise(exercise);
  const previous = index > 0 ? selectedExercises[index - 1] : null;
  const showLink = index > 0
    && exercise.set_type
    && exercise.set_type !== 'normal'
    && previous?.set_type === exercise.set_type;

  return (
    <>
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
        {!pyramid && (
          <Field label="Serie">
            <input
              type="number"
              min="1"
              value={isCardio ? (exercise.serie || 1) : (exercise.serie ?? '')}
              onChange={(e) => {
                if (isCardio) {
                  onUpdate(index, 'serie', parseInt(e.target.value, 10) || 1);
                  return;
                }
                const value = e.target.value;
                onUpdate(index, 'serie', value === '' ? '' : parseInt(value, 10) || '');
              }}
              onBlur={(e) => {
                if (!isCardio && (e.target.value === '' || e.target.value === '0')) {
                  onUpdate(index, 'serie', 1);
                }
              }}
              onFocus={(e) => e.target.select()}
              className={numClass}
            />
          </Field>
        )}

        {!pyramid && isCardio && (
          <Field label="Minuti">
            <input
              type="number"
              min="0"
              placeholder="20"
              value={exercise.ripetizioni_max}
              onChange={(e) => onUpdate(index, 'ripetizioni_max', parseInt(e.target.value, 10) || 0)}
              onFocus={(e) => e.target.select()}
              className={numClass}
            />
          </Field>
        )}

        {!pyramid && !isCardio && (
          <TemplateRepsInputs
            exercise={exercise}
            onUpdate={(field, value) => onUpdate(index, field, value)}
          />
        )}

        {!pyramid && !isCardio && (
          <Field label="Peso (kg)">
            <input
              type="number"
              min="0"
              step="0.5"
              value={exercise.peso_suggerito ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                onUpdate(index, 'peso_suggerito', value === '' ? '' : parseFloat(value) || '');
              }}
              onBlur={(e) => {
                if (e.target.value === '') {
                  onUpdate(index, 'peso_suggerito', 0);
                }
              }}
              onFocus={(e) => e.target.select()}
              className={weightClass}
            />
          </Field>
        )}

        <RecoveryMinSecInputs
          layout="templateGrid"
          templateFieldSize="sm"
          gymDarkInputs
          valueSeconds={exercise.tempo_recupero}
          onSecondsChange={(sec) => onUpdate(index, 'tempo_recupero', sec)}
          defaultTotalSeconds={90}
          focusColor="indigo"
        />

        <Field label="Tipo">
          <select
            value={exercise.set_type}
            onChange={(e) => onUpdate(index, 'set_type', e.target.value)}
            className={selectClass}
          >
            {GYM_WORKOUT_SET_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </Field>
      </div>

      <PyramidSeriesEditor
        exercise={{ ...exercise, id: rowId, ripetizioni: exercise.ripetizioni_max, peso: exercise.peso_suggerito }}
        onUpdate={(_id, field, value) => onUpdate(index, field, value)}
        gymDarkInputs
      />

      <div className="mt-3">
        <label className={labelClass}>Note Trainer</label>
        <textarea
          value={exercise.notes?.trainer ?? ''}
          onChange={(e) => {
            const existingNotes = exercise.notes || { trainer: null, user: null, system: null };
            onUpdate(index, 'notes', {
              trainer: e.target.value || null,
              user: existingNotes.user || null,
              system: existingNotes.system || null,
            });
          }}
          rows={2}
          maxLength={500}
          className={`mt-0 w-full px-2 py-1.5 ${controlClass}`}
          placeholder="Aggiungi istruzioni per l'utente (es: 'Mantieni la schiena dritta', 'Arrivare al massimo')"
        />
      </div>

      {!isCardio && (
        <div className="mt-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={exercise.is_isometric_scheda || false}
              onChange={(e) => onUpdate(index, 'is_isometric_scheda', e.target.checked)}
              className="h-3.5 w-3.5 rounded text-indigo-600"
            />
            <span className="ml-2 text-xs text-gray-700 dark:text-white/85">
              Esercizio isometrico (secondi invece di ripetizioni)
            </span>
          </label>
        </div>
      )}

      {showLink && (
        <div className="mt-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={exercise.linked_to_previous}
              onChange={(e) => onUpdate(index, 'linked_to_previous', e.target.checked)}
              className="h-3.5 w-3.5 rounded text-indigo-600"
            />
            <span className="ml-2 text-xs text-gray-700 dark:text-white/85">
              <Link2 className="mr-1 inline h-3 w-3" />
              Collega all&apos;esercizio precedente ({exercise.set_type})
            </span>
          </label>
        </div>
      )}
    </>
  );
}
